import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { DropdownBaseQueryInterface } from '@zaoblako/shared/common/dtos/dropdown-base-query.dto';
import { DEFAULT_LIMIT, DEFAULT_OFFSET } from '@zaoblako/shared/common/dtos/pagination-query.dto';
import { PaginationFindInterface } from '@zaoblako/shared/common/dtos/pagination-find.dto';
import { EXTERNAL_USER_WIDGET_REGISTRY_SELECT } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-registry-select.dto';
import type { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import type { ExternalUserRegistryQueryInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-registry-query.dto';
import { ExternalUserStatusEnum } from '@zaoblako/shared/modules/security/enums/external-user-status.enum';
import { ExternalUserService } from '@zaoblako/web/core/services/security/external-user.service';
import { BaseMultiSelectComponent } from '@zaoblako/web/shared/components/base-multi-select/base-multi-select.component';
import type { DropdownQueryInterface } from '@zaoblako/web/shared/components/base-multi-select/base-multi-select.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { isStrictHydrationRequest } from '@zaoblako/web/shared/helpers/include-ids-pagination.helper';
import { Observable } from 'rxjs';

@Component({
  selector: 'crm-external-user-multi-select',
  standalone: true,
  imports: [CommonModule, MultiSelectComponent, SpinnerComponent],
  templateUrl: './external-user-multi-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExternalUserMultiSelectComponent
  extends BaseMultiSelectComponent<ExternalUserFindInterface>
  implements OnInit, OnChanges {
  public availableModule = 'BASIS';
  protected readonly entityCacheKind = 'external-user';

  @Input() public clientId: number | null = null;

  /**
   * When true, skip registry load until `clientId` is set
   * (avoids a useless unfiltered fetch on Helpdesk form init).
   */
  @Input() public requireClientId = false;

  public constructor(private readonly externalUserService: ExternalUserService) {
    super();
  }

  public override ngOnInit(): void {
    if (this.requireClientId && !this.clientId) {
      return;
    }
    super.ngOnInit();
  }

  public override ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    if (!changes.clientId) {
      return;
    }

    if (this.requireClientId && !this.clientId) {
      this.entities = [];
      this.cdRef.markForCheck();
      return;
    }

    // Subsequent client changes only — initial load is handled by ngOnInit when clientId is already set
    if (!changes.clientId.firstChange) {
      this.firstLoad();
    }
  }

  public findWithPagination(
    queryParams: Partial<DropdownBaseQueryInterface>
  ): Observable<PaginationFindInterface<ExternalUserFindInterface>> {
    return this.externalUserService.findRegistryItems({
      ...queryParams,
      limit: queryParams?.limit ?? DEFAULT_LIMIT,
      offset: queryParams?.offset ?? DEFAULT_OFFSET,
      select: [...EXTERNAL_USER_WIDGET_REGISTRY_SELECT],
      clientIds: this.clientId ? [this.clientId] : undefined
    } as ExternalUserRegistryQueryInterface);
  }

  protected override getRegistryCacheContext(): Record<string, unknown> {
    return { clientId: this.clientId };
  }

  protected override augmentHydrationQuery(
    query: Partial<DropdownQueryInterface<ExternalUserFindInterface['id']>>
  ): Partial<DropdownQueryInterface<ExternalUserFindInterface['id']>> {
    if (!isStrictHydrationRequest(query)) {
      return query;
    }

    return {
      ...query,
      statuses: [ExternalUserStatusEnum.ACTIVE, ExternalUserStatusEnum.BANNED]
    } as Partial<DropdownQueryInterface<ExternalUserFindInterface['id']>>;
  }
}
